import PropTypes from 'prop-types';
import { Card, Stack, Typography, Box, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';

const getSparkPath = (type) => {
  if (type === 'upStrong') return 'M4 26 C24 20 54 14 84 10 C104 8 122 10 132 12';
  if (type === 'upDown') return 'M4 24 C24 18 42 14 58 18 C76 24 94 22 112 18 C124 16 130 20 134 24';
  if (type === 'downUp') return 'M4 14 C20 20 38 24 56 26 C80 28 100 24 118 20 C126 18 132 16 134 18';
  return 'M4 26 C24 22 46 18 68 16 C92 14 112 12 132 14';
};

const resolveThemeColor = (theme, color, fallback) => {
  if (!color) {
    return fallback;
  }

  if (typeof color !== 'string') {
    return color;
  }

  const [paletteKey, tone] = color.split('.');

  if (tone) {
    return theme.palette[paletteKey]?.[tone] || fallback;
  }

  return theme.palette[color]?.main || color;
};

export default function WebhookMetricCard({ metric, isLoading = false }) {
  const theme = useTheme();
  const sparkColor = theme.palette[metric.color]?.main || theme.palette.primary.main;
  const valueColor = resolveThemeColor(theme, metric.valueColor, sparkColor);
  const labelColor = resolveThemeColor(theme, metric.labelColor, theme.palette.text.primary);

  return (
    <Card
      sx={{
        height: 96,
        px: 2.5,
        py: 2,
        borderRadius: 2,
        bgcolor: 'background.paper',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        overflow: 'hidden',
      }}
    >
      <Stack spacing={0.5}>
        {isLoading ? (
          <>
            <Skeleton variant="text" width={88} height={42} />
            <Skeleton variant="text" width={72} height={22} />
            <Skeleton variant="text" width={56} height={18} />
          </>
        ) : (
          <>
            <Typography
              variant="h3"
              sx={{
                color: valueColor,
                lineHeight: 1.1,
                fontWeight: 'bold',
              }}
            >
              {metric.value}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                fontSize: 13,
                color: labelColor,
              }}
            >
              {metric.label}
            </Typography>

            {metric.badge && (
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ mt: 0.25 }}
              >
                <Box
                  component="img"
                  src="/assets/icons/apps/ic_up.svg"
                  alt=""
                  sx={{ width: 14, height: 14 }}
                />
                <Typography
                  variant="caption"
                  sx={{ color: sparkColor, fontWeight: 500 }}
                >
                  {metric.badge}
                </Typography>
              </Stack>
            )}
          </>
        )}
      </Stack>

      <Box
        sx={{
          width: 92,
          height: 48,
          mr: 0.5,
          display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        }}
      >
        {isLoading ? (
          <Skeleton variant="rounded" width={92} height={48} />
        ) : (
          <Box
            component="svg"
            viewBox="0 0 140 40"
            preserveAspectRatio="none"
            sx={{ width: '100%', height: '100%' }}
          >
            <path
              d={getSparkPath(metric.spark)}
              fill="none"
              stroke={sparkColor}
              strokeWidth="2.4"
              strokeLinecap="round"
            />
          </Box>
        )}
      </Box>
    </Card>
  );
}

WebhookMetricCard.propTypes = {
  isLoading: PropTypes.bool,
  metric: PropTypes.shape({
    key: PropTypes.string,
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    badge: PropTypes.string,
    color: PropTypes.string.isRequired,
    labelColor: PropTypes.string,
    valueColor: PropTypes.string,
    spark: PropTypes.string.isRequired,
  }).isRequired,
};
